import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { executeGetFacility } from '../../dataAccess/webApi/dao/facilitiesDao'
import { executeGetUsageRecord } from '../../dataAccess/webApi/dao/usageRecordsDao'
import { GetUseReasonDto } from '../../dataAccess/webApi/dto/useReasonDto'
import { fromApiYmd, fromApiYmdHms, toApiYmd, toApiHm } from '../../utils/dateUtil'
import { nullPropsToUndefined } from '../../utils/objectUtil'
import { formatLocaleTimeAmountHm } from '../../utils/timeUtil'
import { facilityReservationSelectionCreateUrl } from '../common/constant/appUrl'
import { yesNo } from '../common/constant/classification'
import { OperationId } from '../common/constant/operationLog'
import { useErrorHandle } from '../common/error/errorHandler'
import { useOperationLog } from '../common/operationLog'
import { showLoading } from '../common/store/slices/application'
import { getUseReason } from '../common/useReason'
import { openWindow } from '../../utils/reactUtil'
import { getUrlPathname, QueryParams } from '../common/transition'
import { ReceptViewUrlQueryParams } from '../receiptView/receiptViewService'
import { receiptViewUrl } from '../common/constant/appUrl'
import { GOING_DATETIME_DISPLAY_METHODS } from '../common/constant/projectMst'
import { executePostDecodeMst } from '../../dataAccess/webApi/dao/decodeMstDao'
import { DecodeMstCategory } from '../common/constant/decodeMst'
import { GetUsageRecordDto } from '../../dataAccess/webApi/dto/usageRecordsDto'

interface UrlParams {
  reservationNo: string
}

interface UsageDetail {
  facilityId: string
  facilityName: string
  usageDate: Date
  useFromDatetime: Date
  useToDatetime: Date
  goingToFacilityTime?: string
  goingHomeTime?: string
  status: string
  usageMinute?: string
  name: string
  kana: string
  tel: string
  email: string

  childId: string
  childName: string
  childKana: string

  useReasonCategory?: string
  useReasonDetailCategory?: string
  useReasonDetailDescription?: string
  lunchFlag: string
  snackFlag: string
  citizenNote?: string
  lunchCount?: number
  snackCount?: number

  amount?: number
  usageFee?: number
  useCouponFee?: number
  extentionFee?: number
  nightlyFee?: number
  lunchFee?: number
  snackFee?: number
  otherFee1?: number
  otherFee2?: number
  otherFee3?: number
  reduction?: number
  otherFeeDisplayName1?: string
  otherFeeDisplayName2?: string
  otherFeeDisplayName3?: string
  actualUpdateDatetime?: string
  usageReceiptViewMethod: string
  childActivityReport?: string

  reservationCancelDatetime?: Date

  todayConditionFacility: {
    bodyTemperatureMeasurementTime1: string | null
    bodyTemperature1: number | null
    bodyTemperatureMeasurementTime2: string | null
    bodyTemperature2: number | null
    bodyTemperatureMeasurementTime3: string | null
    bodyTemperature3: number | null
    meal: string | null
    eatingMethod: string | null
    milkTime1: string | null
    milkAmount1: string | null
    milkTime2: string | null
    milkAmount2: string | null
    milkTime3: string | null
    milkAmount3: string | null
    mealTime1: string | null
    mealWhat1: string | null
    mealTime2: string | null
    mealWhat2: string | null
    sleep: string | null
    sleepFromTime1: string | null
    sleepToTime1: string | null
    sleepFromTime2: string | null
    sleepToTime2: string | null
    sleepFromTime3: string | null
    sleepToTime3: string | null
    defecation: string | null
    defecationHours1: string | null
    defecationCondition1: string | null
    defecationHours2: string | null
    defecationCondition2: string | null
    defecationHours3: string | null
    defecationCondition3: string | null
    todayConditionNote: string | null
    note: string | null
    status: string | null
    sendMailDatetime: string | null
    updateDatetime: string | null
  }
}

interface FacilityInfo {
  lunchAcceptFlag: string
  snackAcceptFlag: string
  todayConditionInputFacilityUsageFlag: string
  todayConditionInputFacilityPublicFlag: string
  todayConditionInputFacilityName: string | null
  todayConditionInputFacilityFields: {
    bodyTemperature: string
    meal: string
    sleep: string
    defecation: string
  }
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory()
  const { reservationNo } = useParams<UrlParams>()
  const { addOperationLog, attachAccessData } = useOperationLog()

  const [usageDetail, setUsageDetail] = useState<UsageDetail>()
  const [facilityInfo, setFacilityInfo] = useState<FacilityInfo>()
  const [useReason, setUseReason] = useState<GetUseReasonDto>()

  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001 })

    dispatch(
      showLoading(
        errorHandle(async () => {
          // デコードマスタ取得
          const [eatingMethodDecodeMstValue, mealDecodeMstValue, defecationDecodeMstValue] = await Promise.all([
            executePostDecodeMst({ category: DecodeMstCategory.EATING_METHOD }),
            executePostDecodeMst({ category: DecodeMstCategory.TODAY_CONDITION_INPUT_FACILITY_MEAL }),
            executePostDecodeMst({ category: DecodeMstCategory.DEFECATION_CONDITION })
          ])

          // 詳細情報取得
          const [result, useReasonMst] = await Promise.all([
            getUsageDetail(reservationNo),
            getUseReason(),
          ])

          //アクセスデータログをアタッチ
          attachAccessData({
            accessData: [
              {
                userIdRegFlag: yesNo.yes,
                childId: result.childId,
                usageDate: toApiYmd(result.usageDate),
                reservationNo,
              },
            ],
          })

          //デコードマスタを介して当日入力の食事内容をラベルに変換
          const eatingMethodValueLables = eatingMethodDecodeMstValue
            ? eatingMethodDecodeMstValue.result.map((item) => {
              return {
                value: item.code,
                label: item.name,
              }
            })
            : null
          const todayConditionInputFacilityMealValueLables = mealDecodeMstValue
            ? mealDecodeMstValue.result.map((item) => {
              return {
                value: item.code,
                label: item.name,
              }
            })
            : null
          const defecationConditionValueLables = defecationDecodeMstValue
            ? defecationDecodeMstValue.result.map((item) => {
              return {
                value: item.code,
                label: item.name,
              }
            })
            : null
          
          const eatingMethodLabel = (eatingMethodValueLables && result.todayConditionFacility.eatingMethod)
            ? eatingMethodValueLables.find(((valueLabel) => valueLabel.value === result.todayConditionFacility.eatingMethod))?.label
            : null
          const mealWhat1Label = (todayConditionInputFacilityMealValueLables && result.todayConditionFacility.mealWhat1)
            ? result.todayConditionFacility.mealWhat1.split(',').map((item) => todayConditionInputFacilityMealValueLables.find(((valueLabel) => valueLabel.value === item))?.label).join(',')
            : null
          const mealWhat2Label = (todayConditionInputFacilityMealValueLables && result.todayConditionFacility.mealWhat2)
            ? result.todayConditionFacility.mealWhat2.split(',').map((item) => todayConditionInputFacilityMealValueLables.find(((valueLabel) => valueLabel.value === item))?.label).join(',')
            : null
          const defecationCondition1Label = (defecationConditionValueLables && result.todayConditionFacility.defecationCondition1)
            ? defecationConditionValueLables.find(((valueLabel) => valueLabel.value === result.todayConditionFacility.defecationCondition1))?.label
            : null
          const defecationCondition2Label = (defecationConditionValueLables && result.todayConditionFacility.defecationCondition2)
            ? defecationConditionValueLables.find(((valueLabel) => valueLabel.value === result.todayConditionFacility.defecationCondition2))?.label
            : null
          const defecationCondition3Label = (defecationConditionValueLables && result.todayConditionFacility.defecationCondition3)
            ? defecationConditionValueLables.find(((valueLabel) => valueLabel.value === result.todayConditionFacility.defecationCondition3))?.label
            : null

          const todayConditionFacility: GetUsageRecordDto['todayConditionFacility'] = {
              ...result.todayConditionFacility,
              eatingMethod: eatingMethodLabel ? eatingMethodLabel : null,
              mealWhat1: mealWhat1Label ? mealWhat1Label : null,
              mealWhat2: mealWhat2Label ? mealWhat2Label : null,
              defecationCondition1: defecationCondition1Label ? defecationCondition1Label : null,
              defecationCondition2: defecationCondition2Label ? defecationCondition2Label : null,
              defecationCondition3: defecationCondition3Label ? defecationCondition3Label : null,
            }
              
          //stateを設定
          setUsageDetail({...result,todayConditionFacility: todayConditionFacility})
          setUseReason(useReasonMst)
          
          const facility = await getFacilityInfo(result.facilityId, toApiYmd(result.usageDate))
          setFacilityInfo(facility)
        })
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const goReservation = useCallback(
    () =>
      usageDetail &&
      history.push(facilityReservationSelectionCreateUrl.url(usageDetail.facilityId, usageDetail.childId)),
    [usageDetail]
  )

  const goReceiptView = useCallback(() => {
    if (!usageDetail || !reservationNo) return
    const props = nullPropsToUndefined({
      reservationNo,
      facilityId: usageDetail.facilityId
    })

    openWindow(
      getUrlPathname<QueryParams<ReceptViewUrlQueryParams>>(
        receiptViewUrl.routerPath,
        props
      )
    )
    console.log(usageDetail, openWindow, props);
  }, [usageDetail, reservationNo])

  return {
    usageDetail,
    facilityInfo,
    goReservation,
    useReason,
    goReceiptView,
  }
}

const getUsageDetail = async (reservationNo: string) => {
  const response = await executeGetUsageRecord(reservationNo)
  const {
    usageDate,
    useFromDatetime,
    useToDatetime,
    goingToFacilityDatetime,
    goingHomeDatetime,
    reservationCancelDatetime,
    usageMinute,
    goingDatetimeDisplayMethods,
    ...other
  } = response.result

  return nullPropsToUndefined({
    ...other,
    usageDate: fromApiYmd(usageDate),
    useFromDatetime: fromApiYmdHms(useFromDatetime),
    useToDatetime: fromApiYmdHms(useToDatetime),
    goingToFacilityTime:
      goingDatetimeDisplayMethods === GOING_DATETIME_DISPLAY_METHODS.USED_ALL_FACILITY
        ? toApiHm(fromApiYmdHms(goingToFacilityDatetime))
        : null,
    goingHomeTime:
      goingDatetimeDisplayMethods === GOING_DATETIME_DISPLAY_METHODS.USED_ALL_FACILITY
        ? toApiHm(fromApiYmdHms(goingHomeDatetime))
        : null,
    reservationCancelDatetime: fromApiYmdHms(reservationCancelDatetime),
    usageMinute: formatLocaleTimeAmountHm(usageMinute),
  })
}

const getFacilityInfo = async (facilityId: string, usageDate: string) => {
  const response = await executeGetFacility(facilityId, { targetDate: usageDate })
  const { lunchAcceptFlag, snackAcceptFlag, todayConditionInputFacilityUsageFlag, todayConditionInputFacilityName, todayConditionInputFacilityPublicFlag, todayConditionInputFacilityFields  } = response.result
  return { lunchAcceptFlag, snackAcceptFlag, todayConditionInputFacilityUsageFlag, todayConditionInputFacilityName, todayConditionInputFacilityPublicFlag, todayConditionInputFacilityFields }
}
